import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Loader2, Mail, Send, Sparkles } from "lucide-react";
import { forgotPassword } from "../store/authSlice";
import { ROUTES } from "../config/constants";

const SUCCESS_MESSAGE =
    "If that email exists, we’ve sent a password reset link. Please check your inbox.";

const EMAIL_ERROR_MESSAGE = "Please enter a valid email address.";

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const ForgotPassword = () => {
    const dispatch = useDispatch();
    const { forgotPasswordStatus } = useSelector((state) => state.auth);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [confirmationMessage, setConfirmationMessage] = useState("");

    const isSubmitting = forgotPasswordStatus === "loading";

    const validateEmail = () => {
        if (!email.trim() || !isValidEmail(email.trim())) {
            setEmailError(EMAIL_ERROR_MESSAGE);
            return false;
        }

        setEmailError("");
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmitting || !validateEmail()) return;

        setConfirmationMessage("");

        try {
            await dispatch(forgotPassword(email.trim())).unwrap();
            setConfirmationMessage(SUCCESS_MESSAGE);
        } catch (error) {
            if (error?.status === 422) {
                setEmailError(EMAIL_ERROR_MESSAGE);
                return;
            }

            toast.error("Something went wrong. Please try again.");
        }
    };

    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center bg-[#F8F8FE] px-3 py-8 sm:px-6 lg:px-8">
            <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[0.95fr_1.05fr]">
                <div className="hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 p-8 text-white lg:flex lg:flex-col lg:justify-between">
                    <div>
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                            <Sparkles size={24} aria-hidden="true" />
                        </div>
                        <h1 className="mt-8 text-3xl font-bold leading-tight">
                            Reset access to your workspace
                        </h1>
                        <p className="mt-4 max-w-sm text-sm leading-6 text-indigo-50">
                            Request a secure password reset link and get back to
                            your outreach workflow.
                        </p>
                    </div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-indigo-100">
                        OptiMind account recovery
                    </p>
                </div>

                <div className="w-full p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-md">
                        <div className="mb-6 text-center sm:mb-8">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 lg:hidden">
                                <Sparkles size={24} aria-hidden="true" />
                            </div>
                            <p className="text-sm font-medium text-indigo-600">
                                Recover your account
                            </p>
                            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                                Forgot Password
                            </h2>
                            <p className="mt-3 text-sm leading-6 text-slate-600">
                                Enter your email and we’ll send you a password
                                reset link.
                            </p>
                        </div>

                        {confirmationMessage && (
                            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium leading-6 text-emerald-700">
                                {confirmationMessage}
                            </div>
                        )}

                        <form
                            onSubmit={handleSubmit}
                            noValidate
                            className="space-y-4"
                        >
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Email
                                </label>
                                <div className="relative">
                                    <Mail
                                        size={18}
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(event) => {
                                            setEmail(event.target.value);
                                            setEmailError("");
                                            setConfirmationMessage("");
                                        }}
                                        onBlur={validateEmail}
                                        className={`w-full rounded-xl border bg-white px-10 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 ${
                                            emailError
                                                ? "border-red-400"
                                                : "border-slate-200"
                                        }`}
                                    />
                                </div>
                                {emailError && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {emailError}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                            >
                                {isSubmitting ? (
                                    <Loader2
                                        size={18}
                                        className="animate-spin"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <Send size={18} aria-hidden="true" />
                                )}
                                {isSubmitting
                                    ? "Sending..."
                                    : "Send reset link"}
                            </button>

                            <Link
                                to={ROUTES.LOGIN}
                                className="block text-center text-sm font-semibold text-indigo-600 transition hover:text-indigo-700"
                            >
                                Back to Login
                            </Link>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
